/**
 * People-level quiz traits for GA4.
 * One value per person: the first dog, plus household facts (dog count, email, portion, box).
 * Sent as user properties so checkout and purchase can be split, and on the wizard events themselves.
 */
(function (factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.WizardGa4Traits = api;
})(function () {
  var AGES = { puppy: 1, adult: 1, senior: 1 };
  var BODIES = { heavy: 1, normal: 1, slim: 1 };
  var PICKY = { everything: 1, easy: 1, picky: 1, very_picky: 1 };
  var PORTIONS = { complete: 1, half: 1, topper: 1, mostly: 1 };
  var BOXES = { medium: 1, groot: 1, extra: 1 };
  var FOODS = ['dry', 'wet', 'frozen', 'raw', 'home'];
  var PLAN_STEPS = {
    plan_overview: 1,
    box_size: 1,
    flavors: 1,
    extras: 1,
    profile: 1,
    plan_onetime: 1
  };

  var refresher = null;

  function weightBand(weight) {
    var kg = Number(weight);
    if (!isFinite(kg) || kg <= 0) return '';
    if (kg < 6) return '1-5';
    if (kg < 11) return '6-10';
    if (kg < 16) return '11-15';
    if (kg < 21) return '16-20';
    if (kg < 31) return '21-30';
    if (kg <= 100) {
      var start = Math.floor((kg - 1) / 10) * 10 + 1;
      return start + '-' + (start + 9);
    }
    return '100+';
  }

  function firstDog(state) {
    if (state && Array.isArray(state.dogs) && state.dogs[0]) return state.dogs[0];
    if (state && state.petData) return state.petData;
    if (state && typeof state.getPetData === 'function') return state.getPetData();
    return null;
  }

  function dogCountLabel(state) {
    if (state && Array.isArray(state.dogs)) {
      var n = state.dogs.length || 1;
      return n >= 4 ? '4+' : String(n);
    }
    return '1';
  }

  function breedType(dog) {
    if (!dog) return '';
    if (dog.breedUnknown || dog.breed === 'dont_know') return 'unknown';
    if (dog.breedMixed || dog.breed === 'mixed') return 'mix';
    if (String(dog.breed || '').trim()) return 'named';
    return '';
  }

  function activityValue(dog) {
    if (!dog) return '';
    if (dog.isWorkingDog || dog.activity === 'workingDog' || dog.activity === 'veryActive') return 'working';
    if (dog.activity === 'indoorInactive') return 'indoor';
    if (dog.activity === 'low' || dog.activity === 'moderate') return dog.activity;
    return '';
  }

  function foodValue(list) {
    if (!Array.isArray(list) || !list.length) return '';
    var picked = [];
    for (var i = 0; i < FOODS.length; i++) {
      if (list.indexOf(FOODS[i]) !== -1) picked.push(FOODS[i]);
    }
    return picked.join('+');
  }

  function stepIsAfter(step, marker, order) {
    if (!Array.isArray(order)) return false;
    var at = order.indexOf(step);
    var mark = order.indexOf(marker);
    return at > -1 && mark > -1 && at > mark;
  }

  function allergyValue(dog, step, order) {
    var list = dog && Array.isArray(dog.allergies) ? dog.allergies : [];
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i] !== 'none') return 'has';
    }
    if (stepIsAfter(step, 'allergies', order)) return 'none';
    return '';
  }

  function emailValue(state) {
    if (!state) return '';
    if (state.skippedEmailWithoutAddress) return 'skipped';
    var raw = state.petData && state.petData.ownerEmail ? String(state.petData.ownerEmail).trim() : '';
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) return 'given';
    return '';
  }

  function withBoxStep(order) {
    var copy = Array.isArray(order) ? order.slice() : [];
    if (copy.indexOf('box_size') === -1) {
      var at = copy.indexOf('calculation');
      if (at >= 0) copy.splice(at + 1, 0, 'box_size');
      else copy.push('box_size');
    }
    return copy;
  }

  function portionValue(state, step, scope) {
    if (!PLAN_STEPS[step]) return '';
    if (scope === 'none' || !scope) return '';
    if (scope === 'wizard2') {
      var chosen = state && state.planBowlValue;
      if (!chosen) return '';
      var vals = state.planBowlOptionValues;
      var half = vals && vals.length >= 2
        ? chosen === vals[1]
        : String(chosen).trim().toLowerCase() === 'half';
      return half ? 'half' : 'complete';
    }
    if (scope === 'per_dog') {
      var dog = firstDog(state);
      var perDog = dog && dog.portion;
      return PORTIONS[perDog] ? perDog : '';
    }
    var household = state && state.planPortion;
    return PORTIONS[household] ? household : '';
  }

  function boxValue(state, step, order, enabled) {
    if (!enabled) return '';
    if (step !== 'box_size' && !stepIsAfter(step, 'box_size', order)) return '';
    var size = state && state.boxSize;
    return BOXES[size] ? size : '';
  }

  function collect(state, options) {
    var opts = options || {};
    var step = opts.step || (state && state.currentStep) || '';
    var order = withBoxStep(opts.stepOrder);
    var props = {};
    var dog = firstDog(state || {});
    props.wizard_dogs = dogCountLabel(state || {});

    if (dog) {
      var band = weightBand(dog.weight);
      if (band) props.wizard_weight = band;
      if (AGES[dog.ageGroup]) props.wizard_age = dog.ageGroup;
      if (PICKY[dog.eating]) props.wizard_picky = dog.eating;
      var breed = breedType(dog);
      if (breed) props.wizard_breed = breed;
      if (BODIES[dog.composition]) props.wizard_body = dog.composition;
      var activity = activityValue(dog);
      if (activity) props.wizard_activity = activity;
      var food = foodValue(dog.currentFood);
      if (food) props.wizard_food = food;
      if (dog.health === 'none' || dog.health === 'some') props.wizard_health = dog.health;
      var allergy = allergyValue(dog, step, order);
      if (allergy) props.wizard_allergy = allergy;
    }

    var email = emailValue(state);
    if (email) props.wizard_email = email;
    var portion = portionValue(state, step, opts.portionScope);
    if (portion) props.wizard_portion = portion;
    var box = boxValue(state, step, order, opts.boxEnabled);
    if (box) props.wizard_box = box;
    return props;
  }

  function fromFlow(flow, step, stepOrder) {
    var state = flow && flow.state ? flow.state : {};
    var config = flow && typeof flow.getConfig === 'function' ? flow.getConfig() : null;
    var flowName = config && config.name ? config.name : '';
    var journey = null;
    if (typeof window !== 'undefined' && window.BzWizard && typeof window.BzWizard.id === 'function' && window.BZ_WIZARD_JOURNEYS) {
      journey = window.BZ_WIZARD_JOURNEYS[window.BzWizard.id()] || null;
    }
    var overview = journey && journey.plan && journey.plan.overview;
    var portionScope = overview && overview.portion_scope ? overview.portion_scope : '';
    if (!journey && (flowName === 'wizard-2' || !Array.isArray(state.dogs))) portionScope = 'wizard2';
    var boxEnabled = !!(journey && journey.plan && journey.plan.box_size && journey.plan.box_size.enabled);
    return collect(state, {
      step: step || state.currentStep || '',
      stepOrder: stepOrder,
      portionScope: portionScope,
      boxEnabled: boxEnabled
    });
  }

  function registerRefresh(fn) {
    refresher = fn;
  }

  function notify() {
    try {
      if (typeof refresher === 'function') refresher();
    } catch (e) {}
  }

  return {
    weightBand: weightBand,
    collect: collect,
    fromFlow: fromFlow,
    registerRefresh: registerRefresh,
    notify: notify
  };
});
